import { CherryPickHadithContent } from '@components/CherryPickHadithContent';
import { getHadithWithTarqeemToggled } from '@components/results/items/helpers';

import useTranslation from '@hooks/useTranslation';
import { useUI } from '@hooks/useUI';
import { Alert, Box, Typography } from '@mui/material';
import { WordConfig, downloadDocFromHits } from '@utils/export';
import { removeTashkeel } from '@utils/tashkeel';

import Modal from './Modal';
import { RoadsHadith } from 'shared/interfaces/hadith';

export default function ExportToWordModal({
    handleClose,
    hadiths,
}: {
    handleClose: () => void;
    hadiths: RoadsHadith[];
}) {
    const { isMobileOrTablet } = useUI();
    const { t } = useTranslation('library');

    function handleSubmit(pickerPicked: Record<string, boolean>) {
        try {
            downloadDocFromHits(hadiths, pickerPicked as WordConfig);
            setTimeout(() => {
                handleClose();
            }, 300);
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <Modal
            title={t('word_config')}
            handleClose={handleClose}
            isFullScreen={isMobileOrTablet}
            hasCloseButton={false}
        >
            <CherryPickHadithContent<RoadsHadith>
                hadith={hadiths[0]}
                submitText={t('download_word')}
                onSubmit={handleSubmit}
                pickers={[
                    {
                        key: 'book_name',
                        job_key: 'book',
                        label: t('book'),
                        row: 1,
                        condition: (currentPicker) => currentPicker.hadith.book_name.length > 1,
                        content: (hadith) => (
                            <Typography
                                fontSize={18}
                                py={1}
                                fontWeight={600}
                                width="fit-content"
                            >
                                {hadith.book_name}
                            </Typography>
                        ),
                    },
                    {
                        key: 'seperator_0',
                        label: '',
                        row: 1,
                        hoax: true,
                        condition: (_, pickers) => {
                            const book = pickers.find((p) => p.key === 'book');
                            const number = pickers.find(
                                (p) => p.key === 'number',
                            );

                            return !!(book?.picked && number?.picked && book.hadith.book_name.length > 1 && number.hadith.number.length > 1);
                        },
                        content: () => (
                            <Typography sx={{ mx: 1 }}>-</Typography>
                        ),
                    },
                    {
                        key: 'number',
                        label: t('number'),
                        row: 1,
                        condition: (currentPicker) => currentPicker.hadith.number.length > 1,
                        content: (hadith) => (
                            <Typography fontWeight={600} fontSize={18}>
                                {hadith.number.join(' / ')}
                            </Typography>
                        ),
                    },
                    {
                        key: 'volume',
                        label: t('volume'),
                        row: 2,
                        condition: (currentPicker) => !!currentPicker.hadith.volume,
                        content: (hadith) => (
                            <Typography
                                fontSize={14}
                                color="text.secondary"
                                mt={-0.75}
                            >
                                {t('volume')}: {hadith.volume}
                            </Typography>
                        ),
                    },
                    {
                        key: 'seperator_1',
                        label: '',
                        row: 2,
                        hoax: true,
                        condition: (_, pickers) => {
                            const volume = pickers.find(
                                (p) => p.key === 'volume',
                            );
                            const page = pickers.find((p) => p.key === 'page');

                            return !!(volume?.picked && page?.picked && volume.hadith.volume && page.hadith.page);
                        },
                        content: () => (
                            <Typography
                                sx={{
                                    mx: 1,
                                    mt: -0.75,
                                    color: 'text.secondary',
                                }}
                            >
                                -
                            </Typography>
                        ),
                    },
                    {
                        key: 'page',
                        label: t('page'),
                        row: 2,
                        condition: (currentPicker) => !!currentPicker.hadith.page,
                        content: (hadith) => (
                            <Typography
                                fontSize={14}
                                color="text.secondary"
                                mt={-0.75}
                            >
                                {t('page')}: {hadith.page}
                            </Typography>
                        ),
                    },
                    {
                        key: 'chapter',
                        label: t('theChapter'),
                        row: 3,
                        condition: (currentPicker) => !!currentPicker.hadith.chapter,
                        content: (hadith) => (
                            <Typography
                                fontSize={14}
                                color="text.secondary"
                                mt={-0.75}
                                sx={{
                                    '@media(max-width: 480px)': {
                                        maxWidth: 130,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            >
                                {hadith.chapter}
                            </Typography>
                        ),
                    },
                    {
                        key: 'seperator_2',
                        label: '',
                        row: 3,
                        hoax: true,
                        condition: (_, pickers) => {
                            const chapter = pickers.find(
                                (p) => p.key === 'chapter',
                            );
                            const subChapter = pickers.find(
                                (p) => p.key === 'subChapter',
                            );

                            return !!(chapter?.picked && subChapter?.picked && chapter.hadith.chapter && subChapter.hadith.sub_chapter);
                        },
                        content: () => (
                            <Typography
                                sx={{
                                    mx: 1,
                                    color: 'text.secondary',
                                    mt: -0.75,
                                }}
                            >
                                -
                            </Typography>
                        ),
                    },
                    {
                        key: 'sub_chapter',
                        job_key: 'subChapter',
                        label: t('subChapter'),
                        row: 3,
                        condition: (currentPicker) => !!currentPicker.hadith.sub_chapter,
                        content: (hadith) => (
                            <Typography
                                fontSize={14}
                                color="text.secondary"
                                mt={-0.75}
                            >
                                {hadith.sub_chapter}
                            </Typography>
                        ),
                    },
                    {
                        key: 'hadith',
                        job_key: 'tashkeel',
                        label: t('tashkeel'),
                        row: 4,
                        relation: ['tarqeem'],
                        style: () => ({
                            my: 2,
                            fontSize: 14,
                            lineHeight: 1.75,
                        }),
                        condition: (currentPicker) => !!currentPicker.hadith.hadith,
                        content: (hadith, relatedPickers) => {
                            const tarqeem = relatedPickers.find(
                                (p) => p.key === 'tarqeem',
                            );

                            return (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: getHadithWithTarqeemToggled(
                                            tarqeem?.picked ?? false,
                                            hadith.hadith,
                                        ),
                                    }}
                                />
                            );
                        },
                        subContent: (hadith, relatedPickers) => {
                            const tarqeem = relatedPickers.find(
                                (p) => p.key === 'tarqeem',
                            );

                            return (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: getHadithWithTarqeemToggled(
                                            tarqeem?.picked ?? false,
                                            removeTashkeel(hadith.hadith),
                                        ),
                                    }}
                                />
                            );
                        },
                    },
                    {
                        key: 'tarqeem',
                        label: t('tarqeem'),
                        relation: 'hadith',
                        row: 4,
                    },
                    {
                        key: 'takhreej',
                        label: t('takhreej'),
                        row: 5,
                        disabled: true,
                    },
                    {
                        key: 'rulings',
                        label: t('rulings'),
                        row: 5,
                        condition: (currentPicker) =>
                            currentPicker.hadith.rulings.length > 0,
                        content: (hadith) => (
                            <Box>
                                {hadith.rulings && hadith.rulings.length > 0 ? (
                                    hadith.rulings.map((ruling, index) => (
                                        <Box key={index}>
                                            <Typography fontSize={12}>
                                                {ruling.ruler}: {ruling.ruling}
                                            </Typography>
                                            <Typography fontSize={12}>
                                                {ruling.book_name} (
                                                {ruling.page}/{ruling.volume})
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography fontSize={12}>
                                        {t('no_rulings_found')}
                                    </Typography>
                                )}
                            </Box>
                        ),
                    },
                ]}
            >
                <Alert severity="info" sx={{ my: 1 }}>
                    <Typography fontSize={12}>
                        {t('word_export_disclaimer')}
                    </Typography>
                </Alert>
            </CherryPickHadithContent>
        </Modal>
    );
}
